import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Flex } from '@televet/kibble-ui/chakra';
import { trpc } from '@/lib/trpc';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toaster } from '@/components/ui/toaster';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import {
  TemplateForm,
  type TemplateFormValues,
  buildTemplateMutationPayload,
} from './-components/TemplateForm';

export const Route = createFileRoute(
  '/_authenticated/emitter-templates/add'
)({
  component: AddTemplate,
});

function AddTemplate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: createTemplate, isPending } = useMutation(
    trpc.emitter.createTemplate.mutationOptions({
      onSuccess: (data) => {
        toaster.success({ title: 'Template created' });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listTemplates']],
        });
        navigate({ to: `/emitter-templates/${data.id}` });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to create template',
          description: error.message,
        });
      },
    })
  );

  const handleSubmit = (values: TemplateFormValues) => {
    const payload = buildTemplateMutationPayload(values);
    if (!payload) {
      toaster.error({
        title: 'Invalid form data',
        description:
          'One or more JSON fields contain invalid syntax. Please review and fix them.',
      });
      return;
    }
    createTemplate(payload);
  };

  return (
    <PageContainer>
      <Flex direction="column" gap={3} width="100%">
        <Breadcrumbs
          items={[
            { label: 'Templates', to: '/emitter-templates' },
            { label: 'New Template' },
          ]}
        />
        <TemplateForm
          title="New Template"
          onSubmit={handleSubmit}
          isSubmitting={isPending}
        />
      </Flex>
    </PageContainer>
  );
}
